import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import {
    AuthenticationResult,
    InteractionRequiredAuthError
} from '@azure/msal-browser';
import { Observable, from } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { IAuthProvider } from '../auth-provider.interface';
import { User } from '../../models/global.model';
import { userDetail, userDetail$ } from '../../constants/app-state';

@Injectable({
    providedIn: 'root'
})
export class EntraAuthProvider implements IAuthProvider {

    private readonly msalService = inject(MsalService);

    private interactionProgress = false;

    public token: WritableSignal<string | null> = signal(null);

    /**
     * Initializes MSAL and handles redirect callback.
     */
    async initialize(): Promise<void> {
        try {
            await this.msalService.instance.initialize();

            await this.handleRedirectCallback();

            this.setActiveAccount();

            if (this.isAuthenticated()) {
                this.scheduleTokenRefresh();
            }
        } catch (error) {
            console.error('MSAL initialization failed:', error);
        }
    }

    /**
     * Starts Azure Entra login.
     */
    async login(): Promise<void> {
        try {

            if (
                this.msalService.instance.getAllAccounts().length <= 0 &&
                !this.interactionProgress
            ) {

                this.interactionProgress = true;

                await this.msalService.loginRedirect({
                    scopes: environment.apiConfig.scopes
                });
            }

        } catch (error) {
            console.error('MSAL login failed:', error);
            throw error;
        }
    }

    /**
     * Gets an access token silently.
     */
    getAccessToken(): Observable<string> {

        const account = this.msalService.instance.getActiveAccount()
            ?? this.msalService.instance.getAllAccounts()[0];

        const request = {
            scopes: environment.apiConfig.scopes,
            account
        };

        return new Observable<string>((observer) => {

            from(this.msalService.instance.acquireTokenSilent(request))
                .subscribe({

                    next: (response: AuthenticationResult) => {

                        this.token.set(response.accessToken);

                        this.populateDetails(response);

                        observer.next(response.accessToken);

                        observer.complete();
                    },

                    error: async (error) => {

                        console.error(error);

                        if (error instanceof InteractionRequiredAuthError) {

                            await this.msalService.loginRedirect({
                                scopes: environment.apiConfig.scopes
                            });

                            return;
                        }

                        observer.error(error);
                    }

                });

        });

    }

    /**
     * Handles Azure redirect callback.
     */
    private handleRedirectCallback(): Promise<void> {

        return this.msalService.instance.handleRedirectPromise()

            .then((response) => {

                if (response) {

                    this.setActiveAccount();

                    this.token.set(response.accessToken);

                    this.populateDetails(response);

                }

            })

            .catch((error) => {

                console.error(error);

            })

            .finally(() => {

                this.interactionProgress = false;

            });

    }

    /**
     * Sets the current MSAL account.
     */
    private setActiveAccount(): void {

        const accounts = this.msalService.instance.getAllAccounts();

        if (accounts.length > 0) {

            this.msalService.instance.setActiveAccount(accounts[0]);

        }

    }

    /**
     * Populates current user details.
     */
    private populateDetails(response: AuthenticationResult): void {

        userDetail.next({

            givenName:
                response.account?.idTokenClaims?.['given_name'] ??
                response.account?.name ??
                null,

            familyName:
                response.account?.idTokenClaims?.['family_name'] ??
                null,

            email:
                response.account?.username ??
                response.account?.idTokenClaims?.preferred_username ??
                null,

            expiresOn: response.expiresOn

        } as User);

    }

    /**
     * Refresh token before expiry.
     */
    private scheduleTokenRefresh(): void {

        userDetail$.subscribe((data) => {

            if (!data.expiresOn) {

                return;

            }

            clearTimeout(data.expiresOn as any);

            const expiresInMs =
                new Date(data.expiresOn).getTime() - Date.now();

            const refreshTime =
                expiresInMs - (2 * 60 * 1000);

            if (refreshTime > 0) {

                data.expiresOn = setTimeout(() => {

                    this.getAccessToken().subscribe({

                        error: () => {

                            this.msalService.loginRedirect({
                                scopes: environment.apiConfig.scopes
                            });

                        }

                    });

                }, refreshTime) as any;

            } else {

                this.msalService.loginRedirect({
                    scopes: environment.apiConfig.scopes
                });

            }

        });

    }

    /**
     * Checks if the user is authenticated.
     */
    isAuthenticated(): boolean {

        return this.msalService.instance.getAllAccounts().length > 0;

    }

    /**
 * Gets the current user's email.
 */
    getCurrentUserEmail(): string | null {
        const account = this.msalService.instance.getActiveAccount()
            ?? this.msalService.instance.getAllAccounts()[0];

        return account?.username
            ?? account?.idTokenClaims?.['preferred_username'] as string
            ?? null;
    }

    /**
     * Gets the current user's roles.
     */
    getCurrentUserRoles(): string[] {
        const account = this.msalService.instance.getActiveAccount()
            ?? this.msalService.instance.getAllAccounts()[0];

        const roles = account?.idTokenClaims?.['roles'];

        return Array.isArray(roles) ? roles : [];
    }

    clearSession(): void {
        this.msalService.instance.setActiveAccount(null);
        this.token.set(null);
    }

    /**
     * Logs out the current user.
     */
    async logout(): Promise<void> {

        await this.msalService.logoutRedirect({

            postLogoutRedirectUri:
                environment.auth.msal.auth.postLogoutRedirectUri

        });

        this.clearSession();

    }

}